import { useMutation, useQuery } from "@tanstack/react-query";
import { customFetcher } from "../useFetch";
import { IInvoice } from "../../interfaces/IInvoice";
import { IInvoiceState } from "../../interfaces/IInvoiceState";
import { IInvoiceType } from "../../interfaces/IInvoiceType";
import { IRecipientOfPayment } from "../../interfaces/IRecipientOfPayment";
import { IDocument } from "../../interfaces/IDocument";
import { IContract } from "../../interfaces/IContract";

export const useGetInvoicesByProject = (idProject: number) => {
    const {
        data: invoices,
        isLoading: isLoadingInvoices,
        refetch: refetchInvoices,
    } = useQuery<IInvoice[]>({
        queryKey: ["invoice/byProject", idProject],
        queryFn: () => customFetcher({ endpoint: `/invoice/byProject/${idProject}` }),
        enabled: !!idProject, // verhindert Ausführung ohne gültige idProject
    });

    return { invoices, isLoadingInvoices, refetchInvoices };
};

export const useGenerateInvoiceDocument = (idInvoice: number | undefined = undefined) => {
    const {
        data: invoiceDocument,
        isLoading: isLoadingInvoiceDocument,
        refetch: refetechInvoiceDocument,
        isFetching: isFetechingInvoiceDocument
    } = useQuery<IDocument>({
        queryKey: ["invoice/generate_document", idInvoice],
        queryFn: () => customFetcher({ endpoint: `/invoice/generate_document/${idInvoice}` }),
        enabled: !!idInvoice
    });

    return { invoiceDocument, isLoadingInvoiceDocument, refetechInvoiceDocument, isFetechingInvoiceDocument };
};

export const useGenerateInvoiceDocumentByFunction = () => {
  const { 
    mutateAsync: getInoiceDocument, 
    isPending: isPendingInvoiceDocument
} =
    useMutation<IDocument, any, IInvoice>({
      mutationFn: (currentObject: IInvoice) =>
        customFetcher({
          endpoint: `/invoice/generate_document/${currentObject.id}`,
        }),
    });

  return {
    getInoiceDocument,
    isPendingInvoiceDocument,
  };
};

export const useCreateByProject = () => {
  const { 
    mutateAsync: createnvoiceByProject, 
    isPending: isPendingCreatenvoiceByProject 
} =
    useMutation({
      mutationFn: (currentObject: IInvoice) =>
        customFetcher({
          endpoint: `/invoice/`,
          method: 'POST',
          body: currentObject,
        }),
    });

  return {
    createnvoiceByProject,
    isPendingCreatenvoiceByProject,
  };
};

export const useUpdateInvoice = () => {
  const { 
    mutateAsync: updateInvoice, 
    isPending: isPendingUpdateInvoice 
} =
    useMutation({
      mutationFn: (currentObject: IInvoice) =>
        customFetcher({
          endpoint: `/invoice/${currentObject.id}`,
          method: 'PUT',
          body: currentObject,
        }),
    });

  return {
    updateInvoice,
    isPendingUpdateInvoice,
  };
};

export const useCreateByContract = () => {
  const { 
    mutateAsync: createByContact, 
    isPending: isPendingCreateByContract 
} =
    useMutation({
      mutationFn: (idContact: number) =>
        customFetcher({
          endpoint: `/invoice/byContract/${idContact}`,
          method: 'POST'
        }),
    });

  return {
    createByContact,
    isPendingCreateByContract,
  };
};

export const useCreateByProjectAccocation = () => {
  const { 
    mutateAsync: createByProjectAccocation, 
    isPending: isPendingCreateByProjectAccocation 
} =
    useMutation({
      mutationFn: (idProject: number) =>
        customFetcher({
          endpoint: `/invoice/byProjectAllocation/${idProject}`,
          method: 'POST'
        }),
    });

  return {
    createByProjectAccocation,
    isPendingCreateByProjectAccocation,
  };
};

export const useCreateInvoiceByMaterialOffers = () => {
  const { 
    mutateAsync: createInvoiceByMaterialOffers, 
    isPending: isPendingCreateInvoiceByMaterialOffers
} =
    useMutation({
      mutationFn: ({ idProject, idOffers }: { idProject: number; idOffers: number[] }) =>
        customFetcher({
          endpoint: `/invoice/byMaterialOffers/${idProject}`,
          method: 'POST',
          body: idOffers
        }),
    });

  return {
    createInvoiceByMaterialOffers,
    isPendingCreateInvoiceByMaterialOffers,
  };
};

export const useGetInvoices = () => {
    const {
        data: invoices,
        isLoading: isLoadingInvoices,
        refetch: refetchInvoices,
        isFetching: isFetchingInvoices
    } = useQuery<IInvoice[]>({
        queryKey: ["invoice/"],
        queryFn: () => customFetcher({ endpoint: `/invoice/` }),
    });

    return { invoices, isLoadingInvoices, refetchInvoices, isFetchingInvoices };
};

export const useGetBankAccounts = () => {
    const {
        data: bankAccounts,
        isLoading: isLoadingBankAccounts,
        refetch: refetchBankAccounts,
    } = useQuery<IRecipientOfPayment[]>({
        queryKey: ["invoice/bank_accounts/"],
        queryFn: () => customFetcher({ endpoint: `/invoice/bank_accounts/` }),
    });

    return { bankAccounts, isLoadingBankAccounts, refetchBankAccounts };
};

export const useGetInvoiceStates = () => {
    const {
        data: invoiceStates,
        isLoading: isLoadingInvoiceStates,
        refetch: refetchInvoiceStates,
    } = useQuery<IInvoiceState[]>({
        queryKey: ["invoice/state/"],
        queryFn: () => customFetcher({ endpoint: `/invoice/state/` }),
    });

    return { invoiceStates, isLoadingInvoiceStates, refetchInvoiceStates };
};

export const useGetInvoiceTypes = () => {
    const {
        data: invoiceTypes,
        isLoading: isLoadingInvoiceTypes,
        refetch: refetchInvoiceTypes,
    } = useQuery<IInvoiceType[]>({
        queryKey: ["invoice/type/"],
        queryFn: () => customFetcher({ endpoint: `/invoice/type/` }),
    });

    return { invoiceTypes, isLoadingInvoiceTypes, refetchInvoiceTypes };
};


export const useUpdateBankAccount = () => {
  const { 
    mutateAsync: updateBankAccount, 
    isPending: isPendingUpdateBankAccount 
} =
    useMutation({
      mutationFn: (BankAccountObject: IRecipientOfPayment) =>
        customFetcher({
          endpoint: `/invoice/bank_accounts/${BankAccountObject.id}`,
          method: 'PUT',
          body: BankAccountObject,
        }),
    });

  return {
    updateBankAccount,
    isPendingUpdateBankAccount,
  };
};

export const useCreateBankAccount = () => {
  const {
    mutateAsync: createBankAccount,
    isPending: isPendingCreateBankAccount,
  } = useMutation({
    mutationFn: (BankAccount: IRecipientOfPayment) =>
      customFetcher({
        endpoint: '/invoice/bank_accounts/',
        method: 'POST',
        body: BankAccount,
      }),
  });

  return {
    createBankAccount,
    isPendingCreateBankAccount,
  };
};

export const useDeleteBankAccount = () => {
  const {
    mutateAsync: deleteBankAccount,
    isPending: isPendingDeleteBankAccount,
  } = useMutation({
    mutationFn: (bankAccount: IRecipientOfPayment) =>
      customFetcher({
        endpoint: `/invoice/bank_accounts/${bankAccount.id}`,
        method: 'DELETE'
      }),
  });

  return {
    deleteBankAccount,
    isPendingDeleteBankAccount,
  };
};


export const useDeleteInvoice = () => {
  const { 
    mutateAsync: deleteInvoice, 
    isPending: isPendingDeleteInvoice 
} =
    useMutation({
      mutationFn: (idInvoice: number) =>
        customFetcher({
          endpoint: `/invoice/${idInvoice}`,
          method: 'DELETE'
        }),
    });

  return {
    deleteInvoice,
    isPendingDeleteInvoice,
  };
};